import { Form, Divider, Row, Spin, Tag, Typography, Col, Select, App, Button, FormInstance, Result, Tooltip } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import React from 'react';

import { useNavigation } from '@App/settings/NavigationProvider';
import { ApplicationState, StateManager } from '@App/settings/StateManager';
import { DynamicFormModal } from '@App/components/forms/DynamicFormModal';
import { handler } from '@App/settings/ApiHandler';
import { hasAccess, permissions } from '@App/settings/navigation';
import { useKeycloak } from '@App/settings/keycloak';
import { PublicFormsData } from './FormsView';
import { deserializeFormDates } from '@App/components/helpers';
import { useErrorModal } from '@App/components/ErrorModalProvider';
import { SectionTabs } from '@App/components/SectionTabs';
import { LdapUserInfo } from "@App/components/UserSearchModalPublic";
import { ProjectAccessCard, buildPermissionsPayload, buildRoleHolderPayload, updateProjectPermissions } from '@App/components/ProjectAccessCard';
import KC from "@App/@types/keycloakTypes";
import { Language } from '@AppRoot';


type FormsTypes = {
  forms: Form.FormType[],
  granted?: Form.FormType[],
  ttr?: Form.FormType[],
}

type GetFormsRes = FormsTypes & {
  faculties?: Responses.FacultyType[],
  // users?: Responses.UserType[]
}
const getPublicFormsData = (kc: KC.KCType, uuid: string | undefined) => handler<GetFormsRes>({
  method: 'GET',
  path: `/v3/forms/${uuid ?? ''}`,
}, kc!)

type PostResponse = Responses.Default & {
  forms: Form.FormType[]
};

const postFormsData = (kc: KC.KCType, payload: API.PayloadType, path:string|boolean = false) => handler<PostResponse>({
  method: 'POST',
  path: `/v1/${path ?? 'fill/form/'}`,
  payload
}, kc!)

const postFormsValues = (kc: KC.KCType, payload: API.PayloadType, slug: string | Form.FormType['slug']) => {
  let newPath: string = 'fill/form/';
  if (['researchProject', 'research_project'].includes(slug)) {
    newPath = 'researchProjects/'
  } else if (['oppetoo', 'ttr'].includes(slug)) {
    newPath = 'oppetoo/'
  }
  console.info('* SEND TO PATH:', newPath, slug)
  return handler<PostResponse>({
    method: 'POST',
    path: `/v1/${newPath}`,
    payload
  }, kc!)
}

const fillExistingResearchProject = (kc: KC.KCType, uuid: string, payload: API.PayloadType) =>
  handler<PostResponse>({ method: 'PUT', path: `/v3/researchProjects/${uuid}`, payload }, kc!);

const getEmptySubmission = (kc: KC.KCType, uuid: string) =>
  handler<Responses.ResearchData & Responses.Default>({ method: 'GET', path: `/v3/researchProject/${uuid}` }, kc!);

export const SingleFormPage: React.FC = () => {
  const { state } = React.useContext<StateManager>(ApplicationState);
  const { isLoading, setLoading } = React.useContext(PublicFormsData);
  const [sharedWithUsers, setSharedWithUsers] = React.useState<LdapUserInfo[]>([]);
  const [responsibleResearcher, setResponsibleResearcher] = React.useState<LdapUserInfo | null>(null);
  // Principal investigator (vastutav täitja) of a Grandiveeb project - read-only
  const [principalInvestigator, setPrincipalInvestigator] = React.useState<LdapUserInfo | null>(null);
  const [isGrandiveebProject, setIsGrandiveebProject] = React.useState<boolean>(false);
  // Which section is shown on research project pages: the form or the access block
  const [activeSection, setActiveSection] = React.useState<'form' | 'access'>('form');
  // Live snapshot of the form values (updated on every change), used to know
  // when the whole project can be submitted from the access tab
  const [liveFormValues, setLiveFormValues] = React.useState<Record<string, unknown> | undefined>(undefined);
  const [isSubmittingAll, setIsSubmittingAll] = React.useState<boolean>(false);
  // The research project form instance, used to validate the form from the access tab
  const researchFormRef = React.useRef<FormInstance | null>(null);

  const {message} = App.useApp();
  const { showError } = useErrorModal();
  // 'forms' translations come from the backend, bundled 'local' translations fill the gaps
  const { t, i18n } = useTranslation(['forms', 'local']);
  const {keycloak} = useKeycloak();
  const loaderData = useLoaderData() as API.GetForm;
  const { handleNavigate: nav } = useNavigation();
  const [searchParams] = useSearchParams();
  const fillUuid = searchParams.get('id');
  const [prefillValues, setPrefillValues] = React.useState<object | undefined>(undefined);

  // State of loading an externally created research project (accessed via ?id=<uuid>).
  // 'loading' - project info is being fetched, the form is hidden until we know it exists
  // 'error'   - project not found or the user lacks permissions, show a notice page instead of the form
  // 'ok'      - project exists, render the (prefilled) form
  const [projectFetchState, setProjectFetchState] = React.useState<'loading' | 'ok' | 'error'>(
    fillUuid ? 'loading' : 'ok'
  );

  React.useEffect(() => {
    if (!fillUuid || !keycloak) return;
    getEmptySubmission(keycloak, fillUuid)
      .then((res) => {
        if (!res || res.error || !res.uuid) {
          console.warn('Failed to load research project', fillUuid, res?.error ?? res);
          setProjectFetchState('error');
          return;
        }
        if (res.form_data && Object.keys(res.form_data).length > 0) {
          // Convert saved date values (stored as strings) back to dayjs objects
          // so the antd DatePicker fields do not crash ("date.isValid is not a function")
          setPrefillValues({
            form_data: deserializeFormDates(res.form_data, loaderData?.form?.fields),
          });
        }
        // Prefill current access info so it can be reviewed, changed and re-submitted
        if (res.shared_with_users?.length) {
          setSharedWithUsers(res.shared_with_users.map((user) => ({
            username: user.username,
            firstName: user.firstName ?? user.firstname ?? '',
            lastName: user.lastName ?? user.lastname ?? '',
            email: user.email,
          })));
        }
        if (res.responsible_researcher?.username) {
          setResponsibleResearcher({
            username: res.responsible_researcher.username,
            firstName: res.responsible_researcher.firstname ?? '',
            lastName: res.responsible_researcher.lastname ?? '',
            email: res.responsible_researcher.email,
          });
        }
        if (res.grandiveeb_uid) {
          setIsGrandiveebProject(true);
        }
        if (res.principal_investigator?.username) {
          setPrincipalInvestigator({
            username: res.principal_investigator.username,
            firstName: res.principal_investigator.firstname ?? '',
            lastName: res.principal_investigator.lastname ?? '',
            email: res.principal_investigator.email,
          });
        }
        setProjectFetchState('ok');
      })
      .catch((err) => {
        // e.g. non-JSON error response (404/403) or network failure
        console.error('Failed to load research project', fillUuid, err);
        setProjectFetchState('error');
      });
  }, [fillUuid, keycloak]);

  const isResearchProject = ['research_project', 'researchProject'].includes(loaderData?.form?.type);

  // Can the whole project be submitted from the access tab?
  // (all required form fields filled + responsible researcher picked)
  const requiredFormFields = (loaderData?.form?.fields ?? []).filter(
    (field) => (field.required ?? field.settings?.required) && !field.settings?.dependencies
  );
  const prefillFormData = (prefillValues as { form_data?: Record<string, unknown> } | undefined)?.form_data;
  const currentFormValues = (liveFormValues ?? prefillFormData ?? {}) as Record<string, unknown>;
  const formValuesComplete = requiredFormFields.every((field) => {
    const value = currentFormValues[field.name];
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
  });
  const canSubmitAll = formValuesComplete && Boolean(responsibleResearcher);

  const handlePost = (formPayload: object) => {
    // Research projects use the guided flow: the form tab only validates
    // (onFinish only fires when valid) and continues to the access step,
    // everything is submitted together from there.
    if (isResearchProject) {
      setActiveSection('access');
      return;
    }

    const formMeta = {
      uuid: loaderData?.form.uuid,
      slug: loaderData?.form?.slug,
      name: loaderData?.form?.name,
    };

    const request = fillUuid
      ? fillExistingResearchProject(keycloak!, fillUuid, {
          action: 'update' as API.ActionType,
          payload: {
            form_uuid: loaderData?.form?.uuid,
            form_slug: loaderData?.form?.slug,
            uuid: fillUuid,
            form_data: formPayload,
            sharedWith: sharedWithUsers,
          }
        })
      : postFormsValues(keycloak!, {
          action: 'new' as API.ActionType,
          payload: {
            form: formMeta,
            values: formPayload,
            sharedWith: sharedWithUsers,
            responsibleResearcher: buildRoleHolderPayload(responsibleResearcher),
          }
        }, loaderData?.form?.slug);

    request
    .then((res) => {
      if (res.error) {
        return;
      }

      if (res.success) {
        message.success(t('forms.success.'+res.success, res.success))
      } else if (fillUuid) {
        message.success(t('forms.success.updated', 'Vorm edukalt täidetud'))
      }

      nav(fillUuid ? '/researchProjects' : '/forms')
    })
    .catch((res) => {
      console.log(res)
      if (res.error) {
        console.log(res.error)
        // nav('/forms')
      }
      else {
        message.info('Palun proovi uuesti')
      }
    })
    .finally(() => {
      setLoading(false);
    });
  };

  type OptItem = DefaultOptionType & {
    search: string
  };

  const facultyOptionItems = React.useMemo(() => {
    const nonCategoryOptions = loaderData?.faculties?.filter((a) => a.category !== true) ?? [];
    return nonCategoryOptions.map(
      ({key, id, ...faculty}, optionIdx: number) => ({
        key: key ?? `${optionIdx + 1}-${faculty.code}`,
        value: faculty.code,
        search: `${faculty.code} ${faculty.labels.toString()}`,
        // label: faculty.labels[i18n.language == 'et' ? 'et' : 'en'],
        label: (
          <>
            <Tag color={'processing'}>
              <b>{faculty.code}</b>
            </Tag>
            <Typography.Text>
              {faculty.labels[i18n.language == 'et' ? 'et' : 'en']}
            </Typography.Text>
          </>
        )
      })
  );
  }, [loaderData.faculties, i18n.language]);

  const filterProjects = (input: string, option?: OptItem) => {
    return option?.search?.toLowerCase().includes(input.toLowerCase()) ||
      (option?.label + '').toLowerCase().includes(input.toLowerCase()) ||
      (option?.value + '').toLowerCase().includes(input.toLowerCase())
    ;
  };

  const handleProjectSelectChange = (value: string[], option: OptItem | OptItem[]) => {
    // Updating the state to keep only the latest value
    const newValue = value.length > 0 ? [value[value.length - 1]] : [];
    // form?.setFieldValue('projectName', newValue);
  };

  const projectsOptionItems: OptItem[] = React.useMemo(() => {
    return loaderData?.projects?.map(
      ({slug, name}) => ({
        value: slug,
        label: t(slug, name),
        search: `${slug} ${name}`
      })
    ) ?? []
  }, [loaderData?.projects, t]);

  // Final submit: validates the current form state and saves the form data and
  // the permissions together, regardless of how the user reached the access tab
  const handleSubmitAll = async () => {
    if (!keycloak) return;
    if (!responsibleResearcher) {
      showError(t('forms.error.responsibleResearcherRequired', 'Vastutav uurija on kohustuslik. Määra projektile vastutav uurija.'));
      return;
    }

    // Validate the form authoritatively (missing/invalid fields -> back to the form)
    let formValues: object | undefined;
    try {
      formValues = await researchFormRef.current?.validateFields();
    } catch {
      showError(t('forms.error.missingFields', 'Palun täida kõik kohustuslikud väljad.'));
      setActiveSection('form');
      return;
    }

    setIsSubmittingAll(true);

    try {
      const formMeta = {
        uuid: loaderData?.form?.uuid,
        slug: loaderData?.form?.slug,
        name: loaderData?.form?.name,
      };

      if (fillUuid) {
        // Existing project: save the form data, then the permissions
        const formRes = await fillExistingResearchProject(keycloak, fillUuid, {
          action: 'update' as API.ActionType,
          payload: {
            form_uuid: loaderData?.form?.uuid,
            form_slug: loaderData?.form?.slug,
            uuid: fillUuid,
            form_data: formValues,
            sharedWith: sharedWithUsers,
            // The responsible researcher is mandatory server-side as well;
            // sent with the form save so the order of the two requests does not matter.
            responsibleResearcher: buildRoleHolderPayload(responsibleResearcher),
          },
        });
        if (formRes.error) {
          return;
        }

        const permRes = await updateProjectPermissions(keycloak, fillUuid, {
          action: 'update' as API.ActionType,
          payload: buildPermissionsPayload(sharedWithUsers, responsibleResearcher, principalInvestigator),
        });
        if (permRes.error) {
          return;
        }
      } else {
        // New project: one request creates it with the form data, the shared
        // users and the responsible researcher
        const createRes = await postFormsValues(keycloak, {
          action: 'new' as API.ActionType,
          payload: {
            form: formMeta,
            values: formValues,
            sharedWith: sharedWithUsers,
            responsibleResearcher: buildRoleHolderPayload(responsibleResearcher),
          },
        }, loaderData?.form?.slug);
        if (createRes.error) {
          return;
        }
      }

      message.success(t('forms.success.projectSubmitted', 'Projekt edukalt esitatud'));
      nav(fillUuid ? '/researchProjects' : '/forms');
    } catch (err) {
      console.error('Failed to submit project:', err);
      message.info(t('forms.error.tryAgain', 'Palun proovi uuesti'));
    } finally {
      setIsSubmittingAll(false);
    }
  };

  // Hide the form while it is being verified that the linked project exists
  if (fillUuid && projectFetchState === 'loading') {
    return (
      <Row justify='center' align='middle' style={{ minHeight: '50vh' }}>
        <Col>
          <Spin size='large' />
        </Col>
      </Row>
    );
  }

  // Project could not be loaded (not found / no permissions / backend error) - do not show the form
  if (fillUuid && projectFetchState === 'error') {
    return (
      <Result
        status='404'
        title={t('forms.title.projectNotFound', 'Projekti ei leitud')}
        subTitle={t('forms.description.projectNotFound', 'Kontrolli, et sul on projekti täitmiseks õigused ning et link on õige.')}
        extra={
          <Button type='primary' onClick={() => nav('/')}>
            {t('forms.btn.toHomepage', 'Avalehele')}
          </Button>
        }
      />
    );
  }

  return (
    <Spin spinning={isLoading}>

      <Row>
        <Col
          sm={{offset: 0, span: 24}}
          md={{offset: 2, span: 20}}
          lg={{offset: 3, span: 18}}
        >
          <Typography.Title level={3}>
            {loaderData.form?.settings?.labelTexts?.[i18n.language as Language] ?? loaderData.form?.title}
          </Typography.Title>
          <Typography.Paragraph type='secondary'>
            {loaderData.form?.settings?.descriptionTexts?.[i18n.language as Language] ?? loaderData.form?.description}
          </Typography.Paragraph>
          {/*           
          <Typography.Text type='secondary' style={{fontSize:'.8em'}}>
            {t('table.column.lastUpdated')} {formatDateTime(formData?.updated ?? null)}
          </Typography.Text>
          */}
        </Col>
      </Row>
      
      {isResearchProject ? (
        <Row>
          <Col
            sm={{offset: 0, span: 24}}
            md={{offset: 2, span: 20}}
            lg={{offset: 3, span: 18}}
          >
            <SectionTabs
              active={activeSection}
              onChange={setActiveSection}
              accessCount={sharedWithUsers.length + (responsibleResearcher ? 1 : 0)}
              attention={!responsibleResearcher}
            />
          </Col>
        </Row>
      ) : (
        <Divider />
      )}

      <div hidden={isResearchProject && activeSection !== 'form'}>
      <Row>
        <Col
          sm={{offset: 0, span: 24}}
          md={{offset: 2, span: 20}}
          lg={{offset: 3, span: 18}}
        >
          <DynamicFormModal
            formData={loaderData.form}
            initialValues={prefillValues}
            disabledFields={prefillValues ? Object.keys((prefillValues as any).form_data) : undefined}
            onFinish={handlePost}
            onFinishFailed={() => showError(t('forms.error.missingFields', 'Palun täida kõik kohustuslikud väljad.'))}
            facultyOptions={facultyOptionItems}
            //@ts-ignore
            projectOptions={projectsOptionItems}
            isAdmin={hasAccess(permissions.admin, state.session.permissions)}
            action='new'
            showResult={false}
            submitLabel={isResearchProject
              ? t('forms.btn.nextAccess', 'Järgmine: ligipääs')
              : undefined}
            onFormReady={(form) => { researchFormRef.current = form; }}
            onUpdate={(allValues) => setLiveFormValues(allValues)}
          >
            { loaderData?.form?.type == 'artikkel_30' && (
              <Form.Item
                name={['faculty_code']}
                label={t('forms.label.forFaculty')}
                rules={[{ required: true }]}
                wrapperCol={{ offset: 1 }}
                initialValue={(facultyOptionItems?.length == 1 && facultyOptionItems?.[0]?.value) ?? undefined}
              >
                <Select
                  key='code'
                  allowClear
                  showSearch
                  placeholder={t('forms.placeholders.selectFacultyParent')}
                  options={facultyOptionItems}
                  // filterSort={(a, b) => a?.code?.localeCompare(b?.code)}
                  filterOption={(input: string, option?: OptItem) => {
                    return option?.search?.toLowerCase().includes(input.toLowerCase()) ||
                      option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ||
                      option?.code?.toLowerCase().includes(input.toLowerCase()) ||
                      false;
                  }}
                />
              </Form.Item>
            )}

          </DynamicFormModal>
        </Col>
      </Row>
      </div>

      {/* Ligipääs block: responsible researcher + shared users, saved separately from the form */}
      {isResearchProject && (
        <div hidden={activeSection !== 'access'}>
        <Row>
          <Col
            sm={{offset: 0, span: 24}}
            md={{offset: 2, span: 20}}
            lg={{offset: 3, span: 18}}
          >
            <ProjectAccessCard
              projectUuid={fillUuid ?? undefined}
              sharedWithUsers={sharedWithUsers}
              onSharedWithUsersChange={setSharedWithUsers}
              responsibleResearcher={responsibleResearcher}
              onResponsibleResearcherChange={setResponsibleResearcher}
              principalInvestigator={principalInvestigator}
              showPrincipalInvestigator={isGrandiveebProject}
              requireResponsibleResearcher
              saveButtonLabel={fillUuid ? t('forms.btn.saveAccessOnly', 'Salvesta ainult ligipääs') : undefined}
              footerHint={canSubmitAll ? (
                <Typography.Paragraph type='secondary' style={{ fontSize: '.9em', marginBottom: '1rem' }}>
                  {t('forms.description.pendingSubmit', 'Vorm on täidetud. „Esita“ salvestab vormi andmed ja ligipääsuõigused koos.')}
                </Typography.Paragraph>
              ) : undefined}
              extraActions={
                <Tooltip
                  title={!canSubmitAll
                    ? t('forms.tooltip.submitDisabled', 'Täida kõik kohustuslikud väljad ja määra vastutav uurija.')
                    : undefined}
                >
                  <span>
                    <Button
                        type={'primary'}
                        shape={'round'}
                        size={'large'}
                        onClick={handleSubmitAll}
                        loading={isSubmittingAll}
                        disabled={!canSubmitAll}
                    >
                      {t('forms.btn.submitAll', 'Esita')}
                    </Button>
                  </span>
                </Tooltip>
              }
            />
          </Col>
        </Row>
        </div>
      )}

    </Spin>
  );
};
